import {RouteProp, useNavigation, useRoute} from '@react-navigation/native';
import React, {useEffect} from 'react';
import {View} from 'react-native';

import AsyncContent from '@/components/AsyncContent';
import ClosingHeader from '@/components/ClosingHeader';
import ModalScreenWrapper from '@/components/ModalScreenWrapper';
import SpinLoader from '@/components/SpinLoader';
import Text from '@/components/Text';
import {useActiveUser} from '@/hooks/useActiveUser';
import TransferRouter from '@/modules/Transfer/TrackTransfer/TransferRouter';
import TransferStateProvider from '@/modules/Transfer/TrackTransfer/TransferStateProvider';
import {areTrackTransferParamsValid} from '@/modules/Transfer/utils';
import {useTrackBySlugQuery} from '@/queries/tracks';
import {useThemedStyles} from '@/theme';
import {RootStackParams, Routes} from '@/types/routes';

import {styles} from './TransferScreen.style';

const TransferScreen = () => {
  const style = useThemedStyles(styles);
  const navigation = useNavigation();
  const {params} = useRoute<RouteProp<RootStackParams, Routes.Transfer>>();
  const {transferId, ...transferParams} = params;

  const user = useActiveUser();

  const {track, query} = useTrackBySlugQuery(params.slug);

  useEffect(() => {
    if (!params.slug || !areTrackTransferParamsValid(params)) {
      navigation.goBack();
    }
  }, [params]);

  if (!params || !areTrackTransferParamsValid(transferParams)) {
    return null;
  }

  return (
    <ModalScreenWrapper
      titleId="transfer.title"
      isFullscreenOnMobile
      desktopSize="l">
      <ClosingHeader />
      <View style={style.container}>
        <AsyncContent
          data={track}
          isLoading={query.isLoading}
          isError={!track}
          renderContent={loadedTrack => (
            <TransferStateProvider
              transferId={transferId}
              track={loadedTrack}
              initialTransferParams={{
                chainId: transferParams.chainId,
                from: transferParams.from,
                to: transferParams.to,
              }}
              trackTransferPayload={{
                slug: transferParams.slug,
                contract: transferParams.contract,
                tokenId: transferParams.tokenId,
                tokenType: transferParams.tokenType,
              }}
              initialUser={user}>
              <TransferRouter />
            </TransferStateProvider>
          )}
          loaderComponent={
            <View style={style.content}>
              <SpinLoader />
            </View>
          }
          errorComponent={
            <View style={style.content}>
              <Text id="notFound" weight="semibold" />
            </View>
          }
        />
      </View>
    </ModalScreenWrapper>
  );
};

export default TransferScreen;
